import { useEffect, useRef, useState } from 'react'
import {
  SCORE_FADE_IN_MS,
  SCORE_COUNT_UP_MS,
  hasScoreCountUpPlayed,
  markScoreCountUpPlayed,
  prefersReducedMotion,
  easeOutCubic,
} from '../../scoreCountUp'

/**
 * The total-points hero's one-per-runtime reveal: the score fades in, counts up
 * from zero, and only then does the current-gameweek delta pill appear.
 *
 * Only the homepage ('self' view) plays the count-up; read-only visits to other
 * players' profiles always show their score immediately.
 */
export function useScoreCountUp(overallScore: number, isSelf: boolean) {
  // Computed once per mount — a plain read of module state, no mutation here, so
  // it's safe if React (StrictMode) calls this render twice.
  const [playScoreCountUp] = useState(
    () => isSelf && !hasScoreCountUpPlayed() && !prefersReducedMotion()
  )
  const [displayedScore, setDisplayedScore] = useState(() =>
    playScoreCountUp ? 0 : overallScore
  )
  const [isScoreRevealed, setIsScoreRevealed] = useState(() => !playScoreCountUp)
  // Gates the total-points delta pill: hidden until the count-up finishes on
  // its first play, shown immediately otherwise — mirrors `isScoreRevealed`
  // above, just one step later in the sequence.
  const [isDeltaRevealed, setIsDeltaRevealed] = useState(() => !playScoreCountUp)
  const isScoreCountUpDoneRef = useRef(!playScoreCountUp)

  useEffect(() => {
    if (!playScoreCountUp) {
      return
    }

    // Consumed as soon as this mount commits to playing it, not when it
    // finishes — a real remount only happens via a tab switch, so by the time
    // any such remount could occur, this one is already the play for this load.
    markScoreCountUpPlayed()

    const revealFrame = requestAnimationFrame(() => setIsScoreRevealed(true))

    let countUpFrame = 0
    const countUpTimer = setTimeout(() => {
      const startTime = performance.now()

      const tick = (now: number) => {
        const progress = Math.min((now - startTime) / SCORE_COUNT_UP_MS, 1)
        setDisplayedScore(Math.round(easeOutCubic(progress) * overallScore))
        if (progress < 1) {
          countUpFrame = requestAnimationFrame(tick)
        } else {
          isScoreCountUpDoneRef.current = true
          setIsDeltaRevealed(true)
        }
      }

      countUpFrame = requestAnimationFrame(tick)
    }, SCORE_FADE_IN_MS)

    return () => {
      cancelAnimationFrame(revealFrame)
      cancelAnimationFrame(countUpFrame)
      clearTimeout(countUpTimer)
    }
    // Deliberately mount-only: this plays once per JS runtime (see
    // ../../scoreCountUp.ts), driven off the `overallScore` captured at that
    // moment — a later score change should update the display directly
    // (below), not restart the count-up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isScoreCountUpDoneRef.current) {
      setDisplayedScore(overallScore)
    }
  }, [overallScore])

  return { displayedScore, isScoreRevealed, isDeltaRevealed }
}
